'use client';

import { Box, Typography } from '@mui/material';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import LocalAtmOutlinedIcon from '@mui/icons-material/LocalAtmOutlined';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { formatCommissionUsdCents } from '../../utils/commissionWallet';
import type { ICampaignWorkspaceData } from '../../redux/buzzbridge/campaignWorkspace.types';

const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_VARIANT = '#adaaaa';
const SURFACE_HI = '#262626';

function StatBox({
    label,
    value,
    accent = '#fff',
    icon,
}: {
    label: string;
    value: string | number;
    accent?: string;
    icon?: React.ReactNode;
}) {
    return (
        <Box
            sx={{
                flex: { xs: '1 1 100%', md: '1 1 0' },
                width: { xs: '100%', md: 'auto' },
                minWidth: 0,
                bgcolor: SURFACE_HI,
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 2,
                px: { xs: 1.75, sm: 2 },
                py: { xs: 1.5, sm: 1.75 },
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, color: ON_VARIANT }}>
                {icon}
                <Typography
                    sx={{
                        fontSize: 10,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: ON_VARIANT,
                    }}>
                    {label}
                </Typography>
            </Box>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: { xs: 20, sm: 22 },
                    color: accent,
                    lineHeight: 1.15,
                }}>
                {value}
            </Typography>
        </Box>
    );
}

type CircuulStats = NonNullable<ICampaignWorkspaceData['circuul_stats']>;
type CommissionStats = NonNullable<ICampaignWorkspaceData['commission_stats']>;
type CircuulMeta = NonNullable<ICampaignWorkspaceData['circuul']>;

function BoxesRow({ children }: { children: React.ReactNode }) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                flexWrap: { md: 'nowrap' },
                gap: 1.5,
                width: '100%',
            }}>
            {children}
        </Box>
    );
}

export function CreatorWorkspacePerformanceBoxes({
    circuulStats,
    commissionStats,
    circuul,
    basePayCents,
}: {
    circuulStats?: CircuulStats | null;
    commissionStats?: CommissionStats | null;
    /** Fallback when stats payload is missing but participant has a Circuul code */
    circuul?: CircuulMeta | null;
    /** Base pay per creator, paid on content approval */
    basePayCents?: number | null;
}) {
    if (circuulStats || circuul) {
        const isWeb = (circuulStats?.kind || circuul?.kind) === 'web_visit';
        const cpaCents = circuulStats?.cpa_cents ?? circuul?.cpa_cents ?? 0;
        const clicks = circuulStats?.clicks ?? 0;
        const conversions = circuulStats?.conversions ?? 0;
        const earnedCents = circuulStats?.total_earned_cents ?? 0;

        return (
            <BoxesRow>
                <StatBox
                    label={isWeb ? 'Pay / visit' : 'Pay / install'}
                    value={formatCommissionUsdCents(cpaCents)}
                    accent={GOLD}
                    icon={<LocalAtmOutlinedIcon sx={{ fontSize: 14 }} />}
                />
                {basePayCents != null && basePayCents > 0 ? (
                    <StatBox
                        label="Base pay"
                        value={formatCommissionUsdCents(basePayCents)}
                        accent={MINT}
                        icon={<PaymentsOutlinedIcon sx={{ fontSize: 14 }} />}
                    />
                ) : null}
                <StatBox
                    label={isWeb ? 'Visits' : 'Installs'}
                    value={conversions}
                    accent={MINT}
                    icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                />
                <StatBox label="Clicks" value={clicks} icon={<TouchAppOutlinedIcon sx={{ fontSize: 14 }} />} />
                <StatBox
                    label="Earned"
                    value={formatCommissionUsdCents(earnedCents)}
                    accent={GOLD}
                    icon={<PaymentsOutlinedIcon sx={{ fontSize: 14 }} />}
                />
            </BoxesRow>
        );
    }

    if (commissionStats) {
        return (
            <BoxesRow>
                <StatBox
                    label={commissionStats.rate != null ? `Rate` : 'Commission'}
                    value={commissionStats.rate != null ? `${commissionStats.rate}%` : '—'}
                    accent={GOLD}
                    icon={<LocalAtmOutlinedIcon sx={{ fontSize: 14 }} />}
                />
                {basePayCents != null && basePayCents > 0 ? (
                    <StatBox
                        label="Base pay"
                        value={formatCommissionUsdCents(basePayCents)}
                        accent={MINT}
                        icon={<PaymentsOutlinedIcon sx={{ fontSize: 14 }} />}
                    />
                ) : null}
                <StatBox
                    label="Sales"
                    value={commissionStats.conversions}
                    accent={MINT}
                    icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                />
                <StatBox
                    label="Pending"
                    value={formatCommissionUsdCents(commissionStats.pending_cents)}
                    accent={GOLD}
                    icon={<PaymentsOutlinedIcon sx={{ fontSize: 14 }} />}
                />
                <StatBox
                    label="Earned"
                    value={formatCommissionUsdCents(commissionStats.total_earned_cents)}
                    accent={GOLD}
                    icon={<PaymentsOutlinedIcon sx={{ fontSize: 14 }} />}
                />
            </BoxesRow>
        );
    }

    return null;
}
